import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { Observable, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { PrismSnackbarService } from '@shure/prism-angular-components';
import { ILogger } from '@shure/shared/angular/utils/logging';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
	public defaultOrgId = '';
	private readonly logger: ILogger;

	constructor(
		@Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
		private oktaIntfService: OktaInterfaceService,
		logger: ILogger,
		private translocoService: TranslocoService,
		private snackBarService: PrismSnackbarService
	) {
		this.logger = logger.createScopedLogger('AuthTokenInterceptor');
		//subscribed to getOrganizationId$ observable to get latest selected organization id
		this.oktaIntfService
			.getOrganizationId$()
			.pipe(
				tap((orgId) => {
					this.defaultOrgId = orgId;
				})
			)
			.subscribe();
	}
	public intercept(req: HttpRequest<unknown>, delegate: HttpHandler): Observable<HttpEvent<unknown>> {
		let reqHeaders = req.headers;
		let modifiedReq = req;
		if (!req.url.includes('s3')) {
			if (!req.url.includes(`${this.oktaAuth.options.issuer}`)) {
				reqHeaders = reqHeaders.set('Authorization', `Bearer ${this.oktaAuth.getAccessToken()}`);
				if (req.method !== 'DELETE') {
					reqHeaders = reqHeaders.set('Content-Type', 'application/json');
				}
			}
		} else if (req.url.includes('s3')) {
			reqHeaders.set('Content-Type', 'multipart/form-data');
		}
		// Add cache-busting query parameter for i18n asset files
		if (req.url.includes('/assets/i18n')) {
			const timestamp = new Date().getTime();
			const url = req.url.includes('?') ? `${req.url}&cb=${timestamp}` : `${req.url}?cb=${timestamp}`;
			modifiedReq = req.clone({ url, headers: reqHeaders });
		} else {
			modifiedReq = req.clone({ headers: reqHeaders });
		}

		return delegate.handle(modifiedReq).pipe(
			catchError((error: unknown) => {
				if (error instanceof HttpErrorResponse && error.status === 504) {
					this.handle504Error(error, req);
				}
				return throwError(() => error);
			})
		);
	}
	/**
	 * Handles HTTP 504 Gateway Timeout errors by logging the error and displaying a user-friendly notification.
	 * @param error - The HTTP error response with status 504
	 * @param req - The original HTTP request that timed out
	 */
	public handle504Error(error: HttpErrorResponse, req: HttpRequest<unknown>): void {
		// Log the error for monitoring
		this.logger.error('504-gateway-timeout', '504 Gateway Timeout', {
			url: error.url,
			method: req.method,
			timestamp: new Date().toISOString()
		});

		// Show user-friendly notification if snackBarService is available
		if (this.snackBarService) {
			// Timeout prevents Angular change detection errors
			setTimeout(() => {
				this.snackBarService?.open({
					message: this.translocoService.translate('cloud.shared.error-labels.service-taking-longer'),
					actionText: this.translocoService.translate('common.snackbar.dismiss')
				});
			}, 0);
		}
	}
}
